import apiClient from "../api/client";
import { isNative } from "./mobile";

/**
 * Fetch a protected file as a Blob via apiClient (Bearer + refresh).
 * Prefer this over raw fetch + localStorage token — demo keys and 401 refresh
 * only work through the axios stack.
 */
export async function fetchAuthenticatedBlob(urlPath, fallbackMessage = "تعذر تحميل الملف") {
  try {
    const response = await apiClient.get(urlPath, {
      responseType: "blob",
      timeout: 60000,
    });
    return response.data;
  } catch (err) {
    throw new Error(await resolveBlobErrorMessage(err, fallbackMessage));
  }
}

export async function blobToObjectUrl(blob) {
  return URL.createObjectURL(blob);
}

/**
 * Save / open a blob on the current platform.
 * - Web: classic <a download>
 * - Native: write to cache + Share sheet (Android WebView ignores <a download>)
 */
export async function saveOrShareBlob(blob, originalName = "attachment") {
  const filename = sanitizeFilename(originalName);

  if (isNative()) {
    try {
      await shareBlobNatively(blob, filename);
      return;
    } catch (err) {
      if (isShareCanceled(err)) return;
      throw new Error(
        err?.message || "تعذر فتح الملف على الموبايل. جرّب مرة أخرى.",
      );
    }
  }

  // Web Share (some Android browsers) before anchor fallback
  if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
    try {
      const file = new File([blob], filename, {
        type: blob.type || guessMimeFromName(filename),
      });
      if (!navigator.canShare || navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: filename });
        return;
      }
    } catch (err) {
      if (err?.name === "AbortError" || isShareCanceled(err)) return;
      // fall through to anchor download
    }
  }

  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Delay revoke so the browser can start the download
    window.setTimeout(() => URL.revokeObjectURL(url), 1500);
  }
}

/**
 * Android Share + FileProvider break on Arabic / special filenames and nested
 * paths. Write an ASCII-safe cache file, resolve a file:// URI, then share.
 */
async function shareBlobNatively(blob, filename) {
  const [{ Filesystem, Directory }, { Share }] = await Promise.all([
    import("@capacitor/filesystem"),
    import("@capacitor/share"),
  ]);

  const ext = getExtension(filename);
  const safePath = `att-${Date.now()}${ext || ".bin"}`;
  const base64 = await blobToBase64(blob);

  await Filesystem.writeFile({
    path: safePath,
    data: base64,
    directory: Directory.Cache,
    recursive: true,
  });

  const { uri } = await Filesystem.getUri({
    directory: Directory.Cache,
    path: safePath,
  });

  const fileUrl = toFileUrl(uri);
  if (!fileUrl) {
    throw new Error("تعذر تجهيز الملف للمشاركة");
  }

  try {
    await Share.share({
      title: filename,
      files: [fileUrl],
      dialogTitle: "فتح / مشاركة المرفق",
    });
    return;
  } catch (err) {
    if (isShareCanceled(err)) return;
    // Older Share plugin builds prefer `url` over `files`
    await Share.share({
      title: filename,
      url: fileUrl,
      dialogTitle: "فتح / مشاركة المرفق",
    });
  }
}

function toFileUrl(uri) {
  const raw = String(uri || "").trim();
  if (!raw) return "";
  if (raw.startsWith("file:")) return raw;
  if (raw.startsWith("/")) return `file://${raw}`;
  return raw;
}

function getExtension(name) {
  const match = String(name || "").match(/(\.[A-Za-z0-9]{1,8})$/);
  return match ? match[1].toLowerCase() : "";
}

function guessMimeFromName(name) {
  const ext = getExtension(name);
  switch (ext) {
    case ".pdf":
      return "application/pdf";
    case ".png":
      return "image/png";
    case ".jpg":
    case ".jpeg":
      return "image/jpeg";
    case ".doc":
      return "application/msword";
    case ".docx":
      return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    case ".xls":
      return "application/vnd.ms-excel";
    case ".xlsx":
      return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    default:
      return "application/octet-stream";
  }
}

function isShareCanceled(err) {
  if (!err) return false;
  if (err.name === "AbortError") return true;
  const msg = String(err.message || err || "").toLowerCase();
  return msg.includes("cancel");
}

function sanitizeFilename(name) {
  const cleaned = String(name || "attachment")
    .replace(/[\\/:*?"<>|]/g, "_")
    .trim();
  return cleaned || "attachment";
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("تعذر قراءة الملف"));
        return;
      }
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error("تعذر قراءة الملف"));
    reader.readAsDataURL(blob);
  });
}

async function resolveBlobErrorMessage(err, fallbackMessage) {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const json = JSON.parse(text);
      const message = json.message || fallbackMessage;
      return Array.isArray(message) ? message.join("\n") : message;
    } catch {
      return fallbackMessage;
    }
  }
  const message = err?.response?.data?.message || err?.message || fallbackMessage;
  return Array.isArray(message) ? message.join("\n") : message;
}
